import { Reveal } from "../components/Reveal";
import { FloatingMotif } from "../components/FloatingMotif";
import { BRAND, mailtoHref } from "../data/site";

const SOCIALS = [
    { label: "Instagram", key: "instagram", href: BRAND.socials.instagram },
    { label: "Pinterest", key: "pinterest", href: BRAND.socials.pinterest },
    { label: "Facebook", key: "facebook", href: BRAND.socials.facebook },
    { label: "YouTube", key: "youtube", href: BRAND.socials.youtube },
];

export default function Contact() {
    return (
        <div data-testid="page-contact" className="bg-white">
            {/* Header */}
            <section className="pt-16 md:pt-24 pb-20 md:pb-28 relative overflow-hidden">
                <FloatingMotif />
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                        <div className="md:col-span-8">
                            <Reveal>
                                <div className="flex items-center gap-4 mb-8">
                                    <span className="h-px w-10 bg-[#111111]" />
                                    <span className="text-[10px] tracking-[0.28em] uppercase font-medium">
                                        Contact · Strategy Call
                                    </span>
                                </div>
                            </Reveal>
                            <Reveal delay={0.05}>
                                <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.98] tracking-tight">
                                    A quiet conversation
                                    <br />
                                    <em>is usually enough.</em>
                                </h1>
                            </Reveal>
                            <Reveal delay={0.15}>
                                <p className="mt-10 text-lg md:text-xl text-[#444444] max-w-2xl leading-relaxed">
                                    We don’t use forms. We prefer to start the
                                    way serious business is usually started —
                                    with a clear, written message. Tell us a
                                    little about your business and what you’re
                                    trying to achieve, and we’ll respond
                                    within one working day.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </section>

            {/* Channels — two big editorial cards */}
            <section className="border-y border-[#dddddd]">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-2">
                        <a
                            href={mailtoHref(
                                "Strategy call enquiry from Pixemance",
                            )}
                            data-testid="contact-email-card"
                            className="group relative block py-20 md:py-32 md:pr-12 md:border-r border-[#dddddd] hover:bg-[#111111] transition-colors duration-700"
                        >
                            <p className="eyebrow group-hover:text-white/60 transition-colors duration-700">
                                Channel · 01
                            </p>
                            <h2 className="mt-6 font-serif text-4xl md:text-6xl text-[#111111] group-hover:text-white leading-tight transition-colors duration-700">
                                Email us
                            </h2>
                            <p className="mt-6 font-serif italic text-xl md:text-2xl text-[#444444] group-hover:text-white/70 transition-colors duration-700">
                                {BRAND.email}
                            </p>
                            <p className="mt-8 max-w-md text-[#444444] group-hover:text-white/70 transition-colors duration-700 leading-relaxed">
                                Best for context-rich enquiries — share what
                                you’re working on, your sector, and the
                                outcome you’d like to see.
                            </p>
                            <div className="mt-12 inline-flex items-center gap-3 text-[13px] tracking-wide font-medium text-[#111111] group-hover:text-white transition-colors duration-700">
                                Open email
                                <span
                                    aria-hidden
                                    className="transition-transform duration-500 group-hover:translate-x-1"
                                >
                                    →
                                </span>
                            </div>
                        </a>
                        <div
                            data-testid="contact-voice-card"
                            className="group relative block py-20 md:py-32 md:pl-12 border-t md:border-t-0 border-[#dddddd]"
                        >
                            <p className="eyebrow">Channel · 02</p>
                            <h2 className="mt-6 font-serif text-4xl md:text-6xl text-[#111111] leading-tight">
                                Talk to our voice agent
                            </h2>
                            <p className="mt-6 font-serif italic text-xl md:text-2xl text-[#444444]">
                                Live conversation. No scheduling.
                            </p>
                            <p className="mt-8 max-w-md text-[#444444] leading-relaxed">
                                Prefer to talk? Tap the floating call icon at
                                the bottom-right of any page to start a
                                short, voice-based conversation with our
                                concierge — instantly.
                            </p>
                            <div className="mt-12 inline-flex items-center gap-3 text-[13px] tracking-wide font-medium text-[#111111]">
                                <span
                                    aria-hidden
                                    className="inline-block w-2 h-2 rounded-full bg-[#111111] animate-pulse"
                                />
                                Available now · bottom-right of screen
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* What to expect */}
            <section className="py-24 md:py-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                        <div className="md:col-span-4">
                            <p className="eyebrow">§ What to expect</p>
                            <Reveal>
                                <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-tight">
                                    A calm,
                                    <br />
                                    <em>useful first reply.</em>
                                </h2>
                            </Reveal>
                        </div>
                        <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                            {[
                                {
                                    n: "01",
                                    t: "Within one working day",
                                    d: "Every email is read and replied to personally — usually within hours, never longer than one working day.",
                                },
                                {
                                    n: "02",
                                    t: "No pitch",
                                    d: "We listen, ask questions, and share an honest view. If we’re not the right fit, we’ll say so.",
                                },
                                {
                                    n: "03",
                                    t: "A written next step",
                                    d: "If it makes sense to continue, you’ll receive a clear, written proposal within a few days.",
                                },
                            ].map((b) => (
                                <Reveal key={b.n}>
                                    <div className="border border-[#dddddd] p-8 h-full hover:-translate-y-1 hover:border-[#111111] transition-all duration-500">
                                        <p className="eyebrow">{b.n}</p>
                                        <h3 className="mt-4 font-serif text-2xl text-[#111111] leading-tight">
                                            {b.t}
                                        </h3>
                                        <p className="mt-5 text-[#444444] leading-relaxed">
                                            {b.d}
                                        </p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Coordinates */}
            <section className="bg-[#111111] text-white py-24 md:py-32">
                <div className="max-w-[1440px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                        <div className="md:col-span-4">
                            <p className="eyebrow text-white/60">
                                § Coordinates
                            </p>
                            <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-tight">
                                Quietly
                                <br />
                                <em className="text-white/60">available.</em>
                            </h2>
                        </div>
                        <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-10 text-base">
                            <div>
                                <p className="eyebrow text-white/60">Email</p>
                                <a
                                    href={mailtoHref("Hello Pixemance")}
                                    data-testid="coordinates-email"
                                    className="mt-3 inline-block font-serif text-2xl link-underline text-white"
                                >
                                    {BRAND.email}
                                </a>
                            </div>
                            <div>
                                <p className="eyebrow text-white/60">Studio</p>
                                <p
                                    data-testid="coordinates-address"
                                    className="mt-3 font-serif text-2xl"
                                >
                                    {BRAND.location}
                                </p>
                            </div>
                            <div>
                                <p className="eyebrow text-white/60">Hours</p>
                                <p className="mt-3 font-serif text-2xl">
                                    Mon — Fri, by appointment
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="mt-16 pt-10 border-t border-white/15 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
                        <p className="md:col-span-4 eyebrow text-white/60">
                            § Elsewhere
                        </p>
                        <ul className="md:col-span-8 flex flex-wrap gap-x-8 gap-y-3 text-[15px]">
                            {SOCIALS.map((s) => (
                                <li key={s.key}>
                                    <a
                                        href={s.href}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`contact-social-${s.key}`}
                                        className="link-underline text-white"
                                    >
                                        {s.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>
        </div>
    );
}
